import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import isDirectOnly from "shared/containers/Filters/isDirectOnly";
import { removeTransferAirportFilters } from "shared/containers/Filters/transferAirportFilters";
import { AppState } from "shared/data/reducers";
import { getJourneyDurationInMinutes } from "shared/lib/functions";

import FormattedMessage from "@components/UI/FormattedMessage";
import CrossIcon from "@components/UI/Icons/Cross";

import { ROUTES } from "@shared/constants";

import FilterBox1 from "./FilterBox1";
import FilterBox2 from "./FilterBox2";
import FilterBox3 from "./FilterBox3";
import FilterBox4 from "./FilterBox4";
import FilterBox5 from "./FilterBox5";
import FilterBoxAccommodationType from "./FilterBoxAccommodationType";
import FilterBoxHotelName from "./FilterBoxHotelName";
import FilterBoxPrice from "./FilterBoxPrice";
import FilterBoxReservationTerms from "./FilterBoxReservationTerms";
import FilterBoxStars from "./FilterBoxStars";
import * as constants from "./constants";
import {
  getCleanTransferFilters,
  getDefaultRanges,
  getDefaultSelectedCheckboxFilterValue,
  getOnlyCheckboxFilterValue,
  getTransportCompaniesForFilter,
  getUsedFilterNames,
} from "./helpers";
import { Props, State } from "./types";

const TRANSPORT_COMPANIES_FILTER_SCOPE = "transport_companies_filter";

const initState: State = {
  showRange: false,
  activeFilterFlightPart: "DEPARTURE",
  activeFilterFlightPartDepartureDeparture: "HIDE",
  activeFilterFlightPartDepartureReturn: "HIDE",
  activeFilterFlightPartReturnDeparture: "HIDE",
  activeFilterFlightPartReturnReturn: "HIDE",
  priceFilter: [],
  timeFilters: {},
  oneWay: constants.initialWay,
  return: constants.initialWay,
  activeFilter: 0, // ONE_WAY, RETURN
};

const HotelsFilters = ({
  query,
  toggleMobileFilters,
  showMobileFilters,
  hotelStarCounts,
  hotelNameOptions,
  mealOptions,
  mealOptionCounts,
  showStarFilter,
  lowestPrice,
  highestPrice,
}: Props) => {
  return (
    <div>
      <div
        className={`filters-background ${showMobileFilters ? "show" : ""}`}
      />

      {showMobileFilters && (
        <div
          role="button"
          onClick={() => toggleMobileFilters()}
          className="filters-close-btn fade-in-delay fade-out-delay"
        >
          <CrossIcon size="sm" color="dark-orange" />
        </div>
      )}

      {showMobileFilters && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className="loader-wrapper" onClick={toggleMobileFilters} />
      )}

      <div
        className={`filters-wrapper hotel-filters-wrapper ${
          showMobileFilters ? "filters-wrapper-show-mobile" : ""
        }`}
      >
        <div className="filters">
          <>
            <FilterBoxPrice
              query={query}
              lowestPrice={lowestPrice}
              highestPrice={highestPrice}
              title="ShareModal.price"
              subTitle="Filters.flightTicketPriceTogether"
              lowestPriceQueryParam="hotelLowestPrice"
              highestPriceQueryParam="hotelHighestPrice"
            />
            {showStarFilter && (
              <FilterBoxStars query={query} hotelStarCounts={hotelStarCounts} />
            )}
            <FilterBoxHotelName
              query={query}
              hotelNameOptions={hotelNameOptions}
            />
            <FilterBoxAccommodationType
              query={query}
              mealOptions={mealOptions}
              mealOptionCounts={mealOptionCounts}
            />
            <FilterBoxReservationTerms query={query} />
          </>
        </div>
      </div>
    </div>
  );
};

const HotelFilters = ({
  query,
  toggleMobileFilters,
  showMobileFilters,
  filtersData,
  mealOptions,
  mealOptionCounts,
}: Props) => {
  return (
    <div>
      <div
        className={`filters-background ${showMobileFilters ? "show" : ""}`}
      />
      {showMobileFilters && (
        <div
          role="button"
          onClick={() => toggleMobileFilters()}
          className="filters-close-btn fade-in-delay fade-out-delay"
        >
          <CrossIcon size="sm" color="dark-orange" />
        </div>
      )}

      {showMobileFilters && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className="loader-wrapper" onClick={toggleMobileFilters} />
      )}

      <div
        className={`filters-wrapper hotel-filters-wrapper ${
          showMobileFilters ? "filters-wrapper-show-mobile" : ""
        }`}
      >
        <div className="filters">
          <FilterBoxPrice
            query={query}
            lowestPrice={filtersData.lowestPrice}
            highestPrice={filtersData.highestPrice}
            title="ShareModal.price"
            subTitle="Filters.flightTicketPriceTogether"
          />
          <FilterBoxAccommodationType
            query={query}
            mealOptions={mealOptions}
            mealOptionCounts={mealOptionCounts}
          />
          <FilterBoxReservationTerms query={query} />
        </div>
      </div>
    </div>
  );
};

const FlightsFilters = ({
  query,
  flightOffers,
  toggleMobileFilters,
  showMobileFilters,
}: Props) => {
  const {
    highestPrice,
    lowestPrice,
    highestAlternativeCurrencyPrice,
    lowestAlternativeCurrencyPrice,
    airportsTransfers,
    transportCompanies,
  } = useSelector((appState: AppState) => ({
    highestPrice: appState.search.highestPrice,
    lowestPrice: appState.search.lowestPrice,
    highestAlternativeCurrencyPrice:
      appState.search.highestAlternativeCurrencyPrice,
    lowestAlternativeCurrencyPrice:
      appState.search.lowestAlternativeCurrencyPrice,
    airportsTransfers: appState.search.airportsTransfers,
    transportCompanies: appState.search.oTransportCompanies,
  }));
  const router = useRouter();
  const [state, setState] = useState<State>(initState);

  useEffect(() => {
    const defaultRanges = getDefaultRanges(
      query,
      router.query.currency ? lowestAlternativeCurrencyPrice : lowestPrice,
      router.query.currency ? highestAlternativeCurrencyPrice : highestPrice,
      true
    );
    setState((stateL) => ({
      ...stateL,
      ...defaultRanges,
    }));

    const normalizedQuery = isDirectOnly(query.max_transfers)
      ? removeTransferAirportFilters(query)
      : query;

    router.replace(
      {
        pathname: ROUTES.RESULTS,
        query: {
          ...normalizedQuery,
          ...getCleanTransferFilters(normalizedQuery, airportsTransfers),
        },
      },
      undefined,
      { shallow: true, scroll: false }
    );
    // eslint-disable-next-line
  }, [airportsTransfers]);

  const initStateRef = {
    ...initState,
    oneWay: { ...state.oneWay },
    return: { ...state.return },
  };

  const resetFilters = () => {
    const newQuery = { ...query };

    const hasDepartureTimeFilters = Object.keys(query).some(
      (key) => key.includes("filter_") && key.includes("departure")
    );

    getUsedFilterNames(query, airportsTransfers, state.activeFilter).forEach(
      (param) => {
        delete newQuery[param];
      }
    );

    Object.keys(newQuery).forEach((key) => {
      if (key.startsWith("timePreference_")) {
        delete newQuery[key];
      }
    });

    const defaultRanges = getDefaultRanges(
      query,
      router.query.currency ? lowestAlternativeCurrencyPrice : lowestPrice,
      router.query.currency ? highestAlternativeCurrencyPrice : highestPrice
    );

    setState({
      ...initStateRef,
      timeFilters: {},
      priceFilter: [defaultRanges.priceFilter[0], defaultRanges.priceFilter[1]],
    });

    const shouldTriggerNewSearch = hasDepartureTimeFilters;

    router.replace(
      {
        pathname: ROUTES.RESULTS,
        query: {
          ...newQuery,
          ...getCleanTransferFilters(newQuery, airportsTransfers),
          ...(shouldTriggerNewSearch ? { date: Date.now() } : {}),
        },
      },
      undefined,
      shouldTriggerNewSearch
        ? { shallow: false, scroll: false }
        : { shallow: true, scroll: false }
    );
  };

  const changeDefaultSelectedCheckboxFilter = (
    key: string,
    code: string,
    allValues: string[]
  ) => {
    const newQuery = getDefaultSelectedCheckboxFilterValue(
      query,
      key,
      code,
      allValues
    );

    if (key.includes("_transfers") && newQuery[key] === undefined) {
      newQuery[key] = allValues.join("-");
    }

    if (key === "transport_companies") {
      if (newQuery[key] === undefined) {
        delete newQuery[TRANSPORT_COMPANIES_FILTER_SCOPE];
      } else {
        newQuery[TRANSPORT_COMPANIES_FILTER_SCOPE] = String(state.activeFilter);
      }
    }

    router.replace(
      {
        pathname: ROUTES.RESULTS,
        query: {
          ...newQuery,
        },
      },
      undefined,
      { shallow: true, scroll: false }
    );
  };

  const changeOnlyCheckboxFilter = (key: string, code: string) => {
    const newQuery = getOnlyCheckboxFilterValue(query, key, code);

    if (key === "transport_companies") {
      newQuery[TRANSPORT_COMPANIES_FILTER_SCOPE] = String(state.activeFilter);
    }

    router.replace(
      {
        pathname: ROUTES.RESULTS,
        query: {
          ...newQuery,
        },
      },
      undefined,
      { shallow: true, scroll: false }
    );
  };

  const changeFilter = (e: any) => {
    const { name, value } = e.target;

    let newQuery = { ...query, [name]: value };

    if (value === undefined) {
      delete newQuery[name];
    }

    if (name === "max_transfers" && isDirectOnly(value)) {
      newQuery = removeTransferAirportFilters(newQuery);
    }

    router.replace(
      {
        pathname: ROUTES.RESULTS,
        query: newQuery,
      },
      undefined,
      { shallow: true, scroll: false }
    );
  };

  const isFiltersApplied =
    getUsedFilterNames(query, airportsTransfers, state.activeFilter).length > 0;
  const filterCompanies = getTransportCompaniesForFilter(
    flightOffers,
    transportCompanies,
    state.activeFilter
  );

  let longestJourneyInMinutes = 0;
  if (flightOffers) {
    flightOffers.forEach((offer) => {
      const flightStream =
        offer.FlightItinerary.FlightStream[state.activeFilter];
      if (flightStream) {
        flightStream.forEach((stream) => {
          const duration = getJourneyDurationInMinutes(stream.JourneyDuration);
          if (duration > longestJourneyInMinutes) {
            longestJourneyInMinutes = duration;
          }
        });
      }
    });
  }

  return (
    <div>
      <div
        className={`filters-background ${showMobileFilters ? "show" : ""}`}
      />

      {showMobileFilters && (
        <div
          role="button"
          onClick={() => toggleMobileFilters()}
          className="filters-close-btn fade-in-delay fade-out-delay"
        >
          <CrossIcon size="sm" color="dark-orange" />
        </div>
      )}

      {showMobileFilters && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className="loader-wrapper" onClick={toggleMobileFilters} />
      )}

      <div
        className={`container filters-wrapper ${
          showMobileFilters ? "filters-wrapper-show-mobile" : ""
        }`}
      >
        <div className="filters">
          <>
            <FilterBox1 query={query} changeFilter={changeFilter} />

            <FilterBox2
              activeFilter={state.activeFilter}
              query={query}
              setState={setState}
              timeFilters={state.timeFilters}
              timeCelling={longestJourneyInMinutes}
            />

            <FilterBox3
              activeFilter={state.activeFilter}
              changeDefaultSelectedCheckboxFilter={
                changeDefaultSelectedCheckboxFilter
              }
              changeOnlyCheckboxFilter={changeOnlyCheckboxFilter}
              query={query}
            />

            <FilterBox4
              activeFilter={state.activeFilter}
              changeDefaultSelectedCheckboxFilter={
                changeDefaultSelectedCheckboxFilter
              }
              changeOnlyCheckboxFilter={changeOnlyCheckboxFilter}
              filterCompanies={filterCompanies}
              query={query}
            />

            <FilterBox5
              query={query}
              priceFilter={state.priceFilter}
              setState={setState}
            />
          </>
        </div>

        {isFiltersApplied && (
          <button
            type="button"
            className="filters-erase-button"
            onClick={resetFilters}
          >
            <FormattedMessage id="Filters.resetFilters" />
          </button>
        )}
      </div>
    </div>
  );
};

const Filters = (props: Props) => {
  const router = useRouter();
  const isHotelSearch = router.pathname === ROUTES.HOTELS;
  const isHotelDetail = router.pathname === ROUTES.HOTEL;

  if (isHotelSearch) {
    return <HotelsFilters {...props} />;
  }
  if (isHotelDetail) {
    return <HotelFilters {...props} />;
  }
  return <FlightsFilters {...props} />;
};

export default Filters;
